import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import 'fontsource-roboto';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

// import Dialog from '@material-ui/core/Dialog';
// import DialogActions from '@material-ui/core/DialogActions';
// import DialogContent from '@material-ui/core/DialogContent';
// import DialogContentText from '@material-ui/core/DialogContentText';
// import DialogTitle from '@material-ui/core/DialogTitle';
// import Slide from '@material-ui/core/Slide';

import EditIcon from '@material-ui/icons/Edit';
import CancelIcon from '@material-ui/icons/Cancel';
import DoneIcon from '@material-ui/icons/Done';

const useStyles = makeStyles(theme => ({
  root: {
    whiteSpace: 'nowrap'
  },
  typography: {
    textDecoration: editable => editable ? 'initial' : 'underline',
    cursor: editable => editable ? 'initial' : 'pointer',
    '&:hover': {
      color: editable => editable ? 'initial' : 'blue',
      '& .MuiTypography-root': {
        color: editable => editable ? 'initial' : 'blue'
      }
    }
  },
  textField: {
    width: 'initial',
    verticalAlign: 'middle',
    '& .MuiInputBase-root' : {
      padding: '2px 8px'
    },
    '& .MuiSelect-root': {
      minWidth: '80px',
      paddingTop: '0px',
      paddingLeft: '0px',
      paddingBottom: '0px',
      fontSize: '0.875rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: '400',
      lineHeight: '1.43',
      letterSpacing: '0.01071em'
    }
  },
  circularProgress: {
    margin: '4px',
    verticalAlign: 'middle'
  }
}));

export default function EditableField(props) {
  const ajax = props.ajax;
  const [value, setValue] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [editable, setEditable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const classes = useStyles(editable);

  useEffect(() => {
    setValue(props.value);
    setDefaultValue(props.value);
  }, [props.value]);

  const edit = e => {
    e.stopPropagation();

    setEditable(true);
  };

  const cancel = e => {
    e.stopPropagation();

    setEditable(false);
    setValue(defaultValue);
  };

  const save = e => {
    e.stopPropagation();

    setEditable(false);
    setIsLoading(true);

    ajax(props.ajaxOp, {
      [props.ajaxParamName]: value,
      [props.ajaxUniqueKeyName]: props.ajaxUniqueKeyValue
    })
    .then(response => {
      if (response.ok) {
        setValue(response.data);
        setDefaultValue(response.data);
        props.onChange({
          what: props.ajaxParamName,
          [props.ajaxUniqueKeyName]: props.ajaxUniqueKeyValue,
          newValue: response.data
        });
      } else
        setValue(defaultValue);

      setIsLoading(false);
    });
  };

  const handleKeyDown = e => {
    switch (e.keyCode) {
      case 13: save(e); break;
      case 27: cancel(e); break;
      default: break;
    }
  };

  return (
    <Box component='div' display='inline' className={classes.root}
      onClick={editable ? null : edit}
    >
      {!editable && !isLoading && props.iconPosition === 'start' &&
        <IconButton size='small' onClick={e => edit(e)}>{props.editIcon}</IconButton>
      }

      <Typography display='inline' className={classes.typography}>
        {`${props.label} `}

        {!editable && !isLoading && props.iconPosition === 'middle' &&
          <IconButton size='small' onClick={e => edit(e)}>{props.editIcon}</IconButton>
        }

        {!editable &&
          <Typography component='span' display='inline' color='textPrimary'>{value}</Typography>
        }
      </Typography>

      {!editable && !isLoading && props.iconPosition === 'end' &&
        <IconButton size='small' onClick={e => edit(e)}>{props.editIcon}</IconButton>
      }

      {editable &&
        <TextField autoFocus className={classes.textField}
          select={props.type === 'select'}
          value={value}
          onClick={e => e.stopPropagation()}
          onFocus={props.type === 'select' ? null : e => e.target.select()}
          onKeyDown={props.type === 'select' ? null : e => handleKeyDown(e)}
          onChange={e => setValue(e.target.value)}
          inputProps={{
            style: {
              padding: '0px',
              textTransform: 'uppercase'
            }
          }}
        >
          {props.type === 'select' && props.options.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.value}
            </MenuItem>
          ))}
        </TextField>
      }

      {editable &&
        <IconButton size='small' onClick={e => cancel(e)}>{props.cancelIcon}</IconButton>
      }

      {editable &&
        <IconButton size='small' onClick={e => save(e)}>{props.saveIcon}</IconButton>
      }

      {!editable && isLoading &&
        <CircularProgress size={18} thickness={5} className={classes.circularProgress} />
      }
    </Box>
  );
}

EditableField.propTypes = {
  type: PropTypes.string,
  options: PropTypes.PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  buttonSize: PropTypes.oneOf(['small', 'medium']),
  iconPosition: PropTypes.oneOf(['start', 'middle', 'end']),
  editIcon: PropTypes.element,
  cancelIcon: PropTypes.element,
  saveIcon: PropTypes.element,
  onChange: PropTypes.func.isRequired,
  ajax: PropTypes.func.isRequired,
  ajaxOp: PropTypes.string.isRequired,
  ajaxParamName: PropTypes.string.isRequired,
  ajaxUniqueKeyName: PropTypes.string.isRequired,
  ajaxUniqueKeyValue: PropTypes.string.isRequired
};

EditableField.defaultProps = {
  type: 'text',
  options: [],
  buttonSize: 'small',
  iconPosition: 'end',
  editIcon: <EditIcon color='action' fontSize='small' />,
  cancelIcon: <CancelIcon color='secondary' fontSize='small' />,
  saveIcon: <DoneIcon color='primary' fontSize='small' />,
  ajaxParamName: 'value',
  ajaxUniqueKeyName: 'id'
};